import { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import SongList from "components/SongList/SongList";
import { rootSelectors, RootState } from "state/rootReducer";
import { actions } from "state/slices/songsSlice";
import {
  actions as userActions,
  SongFeedbackAndSeed,
} from "state/slices/userSlice";
import { formatDuration } from "shared/formatDuration";
import { LoadingSpinner } from "components/LoadingSpinner/LoadingSpinner";
import SongActionBar from "components/SongActionBar/SongActionBar";
import spotifyLogo from "layout/SpotifyBar/assets/spotify-logo-full.png";
import spotifyLogoSmall from "layout/SpotifyBar/assets/spotify-logo.png";
import { useGoBack, useOpenSong } from "shared/navigation";

const ATTRIBUTION_CLASS =
  "hidden sm:flex flex-row md:flex-col items-center md:items-end justify-end gap-4 " +
  "flex-none self-stretch md:self-center pt-4 md:pt-0 " +
  "border-t md:border-t-0 border-rule text-xs text-muted";

interface OwnProps {
  song: Song;
  artUri: string | null;
  artistUris: string[];
  spotifyUri: string | null;
  album: string | null;
  albumUri: string | null;
  fetchRecommendationsHandler: (
    song: Song,
    search: boolean,
    algo: "v1" | "v2"
  ) => void;
  includeScore: boolean;
}

interface StateProps {
  recommendations: SongRecommendation[] | null;
  songFeedbackByGid: Record<string, SongFeedbackAndSeed> | null;
  isLoggedIn: boolean;
}

interface DispatchProps {
  onFetchSongFeedback: typeof userActions.fetchSongFeedback;
  onAddFeedback: typeof userActions.addFeedback;
  onAddToPlaylist: typeof actions.addToPlaylist;
}

export type SongDetailProps = OwnProps &
  StateProps &
  ResolveThunks<DispatchProps>;

export function SongDetail(props: SongDetailProps): JSX.Element {
  const refreshRecsInterval = useRef<NodeJS.Timeout | null>(null);
  const [fetching, setFetching] = useState(false);
  const [algo, setAlgo] = useState<"v1" | "v2">("v2");
  const goBack = useGoBack();
  const openSong = useOpenSong();

  useEffect(() => {
    if (
      !refreshRecsInterval.current &&
      props.recommendations &&
      props.recommendations.length > 0
    ) {
      startRecFetcher(algo);
    }

    const toClear = refreshRecsInterval.current;

    return () => {
      if (toClear) {
        console.log("Clearing rec fetcher: " + toClear);

        clearInterval(toClear);

        refreshRecsInterval.current = null;
      }
    };
  }, [refreshRecsInterval.current]);

  useEffect(() => {
    if (props.songFeedbackByGid === null) {
      props.onFetchSongFeedback();
    }
  }, [props.songFeedbackByGid === null]);

  useEffect(() => {
    if (props.recommendations !== null) {
      setFetching(false);
    }
  }, [props.recommendations]);

  function startRecFetcher(algo: "v1" | "v2"): void {
    refreshRecsInterval.current = setInterval(
      () => props.fetchRecommendationsHandler(props.song, false, algo),
      120_000
    );

    console.log("Started fetcher: " + refreshRecsInterval.current);
  }

  function fetchRecs(algo: "v1" | "v2"): void {
    setFetching(true);

    props.fetchRecommendationsHandler(props.song, true, algo);

    setAlgo(algo);
    startRecFetcher(algo);
  }

  function render(): JSX.Element {
    // No overflow clamp here: <main> in AppRouter is the scroll container now,
    // and a nested `overflow: hidden` would clip the recommendations list.
    return (
      <div className="w-full flex flex-col gap-4 p-3 sm:p-4">
        {/* Phones get Back in the app header instead (AppHeader), so it
            doesn't take a row of its own above the song. */}
        <div className="hidden sm:block">
          <button
            type="button"
            className="button-small text-sm px-2 py-1 -ml-1"
            onClick={goBack}
          >
            ← Back
          </button>
        </div>
        {renderSongDetails()}
        {renderRecommendationsSection()}
      </div>
    );
  }

  function renderSongDetails(): JSX.Element {
    const artists = props.song.artists;

    // Spotify's attribution for the metadata shown here. Required, so it stays.
    const poweredBy = (
      <>
        <span>Song details powered by</span>
        <img
          src={spotifyLogo}
          alt="Spotify"
          width="96"
          height="29"
          className="w-24 h-auto"
        />
      </>
    );

    return (
      <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8 p-4 bg-surface border border-rule rounded-token">
        {/* A grid, so the controls can change place without duplicating them.
            Phones: art beside the title and metadata, then the duration and
            actions on a full-width row beneath — the text column beside the
            art is too narrow for them, and the tags ran off the screen.
            From sm: art spans both rows and everything sits in the text
            column, as before. */}
        <div className="grid grid-cols-[auto_minmax(0,1fr)] sm:grid-rows-[auto_1fr] gap-x-4 gap-y-3 min-w-0 flex-1">
          {/* A fixed box either way, so the card doesn't reflow when art loads. */}
          {props.artUri && props.spotifyUri ? (
            <a
              href={props.spotifyUri}
              target="_blank"
              rel="noreferrer"
              className="group flex-none self-start sm:row-span-2"
            >
              <img
                className="w-20 h-20 sm:w-28 sm:h-28 rounded object-cover transition-opacity group-hover:opacity-90"
                src={props.artUri}
                width="112"
                height="112"
                alt={props.album ? `${props.album} album art` : "Album art"}
              />
            </a>
          ) : (
            <div
              className="w-20 h-20 sm:w-28 sm:h-28 rounded bg-raised flex-none self-start sm:row-span-2"
              aria-hidden="true"
            ></div>
          )}

          <div className="flex flex-col gap-2 min-w-0 self-center sm:self-start">
            {/* One type scale plus a two-line clamp, replacing szToClassFn —
                which picked sizes by string length and returned `text-md`,
                a class that has never existed in Tailwind. */}
            <h1 className="text-xl sm:text-2xl font-bold text-ink leading-tight line-clamp-2 m-0">
              <a
                href={props.spotifyUri ?? "https://open.spotify.com"}
                target="_blank"
                rel="noreferrer"
              >
                {props.song.name}
              </a>
            </h1>

            {/* The link back to Spotify sits with the metadata it opens, and
                wraps beneath it when the line runs out of room. */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <p className="text-sm text-ink-2 leading-snug m-0 min-w-0">
                {artists.map((artist, i) => (
                  <span key={artist.gid}>
                    <a
                      href={props.artistUris?.[i] ?? "https://open.spotify.com"}
                      target="_blank"
                      rel="noreferrer"
                      className="text-ink"
                    >
                      {artist.name}
                    </a>
                    {i !== artists.length - 1 ? ", " : ""}
                  </span>
                ))}
                {props.album ? (
                  <>
                    <span className="text-muted" aria-hidden="true">
                      {" · "}
                    </span>
                    <a
                      href={props.albumUri ?? "https://open.spotify.com"}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {props.album}
                    </a>
                  </>
                ) : null}
              </p>
              {/* "Listen on Spotify" is one of the link labels Spotify's design
                guidelines allow ("Open Spotify", "Play on Spotify", "Listen
                on Spotify"); the previous "Open in Spotify" is not. */}
              <a
                className="bordered flex items-center gap-2 pl-1.5 pr-2.5 py-1 text-sm whitespace-nowrap"
                href={props.spotifyUri ?? "https://open.spotify.com"}
                target="_blank"
                rel="noreferrer"
              >
                {/* Spotify's minimum for the icon is 21px on screen. */}
                <img
                  src={spotifyLogoSmall}
                  alt=""
                  width="22"
                  height="22"
                  className="w-[22px] h-[22px]"
                />
                Listen on Spotify
              </a>
            </div>
          </div>

          <div className="col-span-2 sm:col-span-1 sm:col-start-2 flex flex-wrap items-center gap-x-3 gap-y-2 min-w-0 self-start">
            <span className="text-sm text-ink-2 tabular-nums">
              {formatDuration(props.song.duration)}
            </span>
            {props.isLoggedIn ? (
              <SongActionBar
                addBorder={false}
                trackId={props.song.gid}
                hasFeedback={!!props.songFeedbackByGid?.[props.song.gid]}
                onAddFeedback={props.onAddFeedback}
                currentFeedback={props.songFeedbackByGid?.[props.song.gid]}
                // So the duration fits on this line on phones, with or
                // without feedback.
                compactOnPhones
                songName={props.song.name}
                onPlaySong={() => {
                  props.onAddToPlaylist([props.song]);
                }}
              />
            ) : null}
          </div>
        </div>

        {/* The attribution stands alone. Spotify's guidelines ask for clear
            space around the logo of half the icon's height (about 15px at
            this size), so nothing comes closer than 16px: not the label
            (gap-4), the card's edge (p-4), the content beside it (md:gap-8)
            or, below md, the rule above it (pt-4). It used to share a
            column with the link button, 4px from its label.
            Between sm and md it's a single right-aligned row under a rule.
            Hidden below sm by request, to save room on phones. The footer's
            text notice is hidden there too, also by request, which leaves only
            the "Listen on Spotify" icon. That's lighter than the attribution
            Spotify's guidelines ask for alongside its metadata. */}
        {props.isLoggedIn ? (
          props.spotifyUri ? (
            <a
              className={ATTRIBUTION_CLASS}
              href={props.spotifyUri}
              target="_blank"
              rel="noreferrer"
            >
              {poweredBy}
            </a>
          ) : (
            <div className={ATTRIBUTION_CLASS}>{poweredBy}</div>
          )
        ) : null}
      </div>
    );
  }

  function renderRecommendationsSection(): JSX.Element {
    if (fetching) {
      return <LoadingSpinner border={false} />;
    }
    if (!props.recommendations) {
      return (
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <p className="text-sm text-ink-2 m-0">
            Find tracks similar to this one.
          </p>
          <button
            type="button"
            className="button-primary"
            onClick={() => {
              fetchRecs("v2");
            }}
          >
            Get recommendations
          </button>
        </div>
      );
    } else if (props.recommendations.length === 0) {
      return (
        <p className="text-sm text-ink-2 text-center py-10 m-0">
          No recommendations found for this track — try a different song.
        </p>
      );
    } else {
      return (
        <div className="flex flex-col gap-2">
          <div className="flex items-baseline gap-2">
            <h2 className="text-sm sm:text-base font-semibold text-ink m-0">
              Recommendations
            </h2>
            <span className="text-xs text-muted tabular-nums">
              {props.recommendations.length}
            </span>
          </div>
          <SongList
            songs={props.recommendations.map((sr) => sr.song)}
            maxSongs={500}
            otherdata={
              props.includeScore
                ? props.recommendations.map((sr) => sr.score + "")
                : undefined
            }
            otherdataName={props.includeScore ? "Score" : undefined}
            selectSongHandler={openSong}
          />
        </div>
      );
    }
  }

  return render();
}

function mapState(state: RootState, { song }: OwnProps): StateProps {
  return {
    isLoggedIn: state.user.currentUser?.id !== undefined,
    recommendations: rootSelectors.songs.getRecommendations(state, song.gid),
    songFeedbackByGid: rootSelectors.user.getSongFeedbackByGid(state),
  };
}

const mapDispatch: DispatchProps = {
  onAddFeedback: userActions.addFeedback,
  onAddToPlaylist: actions.addToPlaylist,
  onFetchSongFeedback: userActions.fetchSongFeedback,
};

export default connect(mapState, mapDispatch)(SongDetail);
